import { TeamMember, Task } from './types';

// ── Deterministic avatar color from user id ───────────────────────────────────

const PALETTE = ['#7551FF', '#01B574', '#FFB547', '#0260CB', '#EA4335', '#34A853', '#FF6D00', '#6D28D9'];

function hashId(id: string): number {
    let h = 0;
    for (let i = 0; i < id.length; i++) h = id.charCodeAt(i) + ((h << 5) - h);
    return Math.abs(h);
}

export function idToColor(id: string): string {
    return PALETTE[hashId(id) % PALETTE.length];
}

export function nameToInitials(fullName: string): string {
    const parts = fullName.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// ── Compute live stats from OKR tasks ────────────────────────────────────────

export function computeMemberStats(userId: string, tasks: Task[]): Pick<
    TeamMember,
    'tasksCompleted' | 'onTimeRate' | 'avgCompletionHours' | 'currentActiveTasks' | 'completionRate'
> {
    const mine = tasks.filter((t) => t.assigneeId === userId);
    const done = mine.filter((t) => t.status === 'done');
    const active = mine.filter((t) => t.status !== 'done');

    const onTime = done.filter((t) => {
        if (!t.completedAt) return true; // assume ok if no date
        return new Date(t.completedAt) <= new Date(t.dueDate);
    });

    // Approximate completion time: use estimatedHours if available, otherwise 0
    const avgHours =
        done.length > 0
            ? Math.round((done.reduce((s, t) => s + (t.estimatedHours || 0), 0) / done.length) * 10) / 10
            : 0;

    return {
        tasksCompleted: done.length,
        onTimeRate: done.length > 0 ? Math.round((onTime.length / done.length) * 100) : 100,
        avgCompletionHours: avgHours,
        currentActiveTasks: active.length,
        completionRate: mine.length > 0 ? Math.round((done.length / mine.length) * 100) : 0,
    };
}

// ── Map raw API user to TeamMember ────────────────────────────────────────────

export function apiUserToMember(user: { _id: string; fullName: string; email?: string }, tasks: Task[]): TeamMember {
    const id = user._id;
    return {
        id,
        name: user.fullName,
        role: user.email ?? '',
        avatarInitials: nameToInitials(user.fullName),
        avatarColor: idToColor(id),
        ...computeMemberStats(id, tasks),
    };
}

// ── Refresh team stats when tasks change ─────────────────────────────────────

export function refreshTeamStats(team: TeamMember[], tasks: Task[]): TeamMember[] {
    return team.map((m) => ({ ...m, ...computeMemberStats(m.id, tasks) }));
}
